import { useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@radix-ui/themes';
import * as Sentry from '@sentry/react';

import Text from '../../../../shared/Text/Text';
import { useNotification } from '../../../../shared/Notification/useNotification';
import { downloadEmailPng } from '../../../../lib/helpers/downloadEmailPng';
import { broadcastFileName } from '../../../../lib/helpers/previewFileName';
import { sanitizeEmailHtml } from '../../utils/emailSanitizer';
import { useBroadcastContext } from '../../contexts/BroadcastContext.jsx';

const ME = '__me__';

/**
 * Mailer iframe src. Preview-as (not Me) is a query on email_preview so
 * the letter, not the React sanitizer, renders that person.
 *
 * @param {string} uuid
 * @param {string} [previewAsUuid]
 * @returns {string}
 */
const emailPreviewSrc = (uuid, previewAsUuid) => {
  const base = `/client_admin/broadcast_messages/${uuid}/email_preview`;
  if (!previewAsUuid || previewAsUuid === ME) return base;
  return `${base}?as_user_uuid=${encodeURIComponent(previewAsUuid)}`;
};

/**
 * The letter the preview iframe shows, as a Document. The iframe is
 * sandboxed to an opaque origin, so its document can't be read; this
 * fetches the same URL instead. A redirect means the letter was refused
 * (signed out, or not this board's), and its target isn't the letter.
 *
 * @param {string} src
 * @returns {Promise<Document>}
 */
const fetchEmailDocument = async src => {
  const response = await fetch(src);
  if (!response.ok || response.redirected) {
    throw new Error(
      `Broadcast email preview request failed (${response.status})`,
    );
  }

  return new DOMParser().parseFromString(await response.text(), 'text/html');
};

/**
 * Broadcast review email preview. Saved drafts use the mailer iframe
 * under the From/Subject chrome. Unsaved drafts keep the sanitized body.
 * Review is only reached after goToStep persists (create or update).
 *
 * @param {object} props
 * @param {string} props.subject
 * @param {string} props.emailBody
 * @param {string} [props.uuid]
 * @param {string} [props.previewAsUuid]
 */
export const EmailPreview = ({
  subject,
  emailBody,
  uuid,
  previewAsUuid: previewAsUuidProp,
}) => {
  const { t } = useTranslation('broadcasts');
  const { showError } = useNotification();
  const boardEmail = useSelector(state => state.currentBoard.email);
  const boardName = useSelector(state => state.currentBoard.name);
  const ctx = useBroadcastContext();
  const previewAsUuid = previewAsUuidProp ?? ctx?.previewAsUuid;
  const [downloading, setDownloading] = useState(false);

  const from = t('email.nameEmail', { name: boardName, email: boardEmail });
  const src = uuid && emailPreviewSrc(uuid, previewAsUuid);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadEmailPng({
        emailDocument: await fetchEmailDocument(src),
        subject,
        from,
        fileName: broadcastFileName({
          board: boardName,
          title: subject,
          channel: 'email',
        }),
      });
    } catch (error) {
      // A capture that fails on an image can reject with a DOM Event.
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error('Broadcast email preview download failed', {
              cause: error,
            }),
      );
      showError({ content: t('email.downloadPngFailed') });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Meta>
          <From>
            <Text variant="button">{t('email.from')}</Text>
            <Text variant="subtitle2">{from}</Text>
          </From>
          <Subject>
            <Text variant="button">{t('email.subjectLabel')}</Text>
            <Text variant="subtitle2">{subject}</Text>
          </Subject>
        </Meta>
        {src && (
          <Button
            size="2"
            variant="outline"
            color="gray"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? t('email.downloadingPng') : t('email.downloadPng')}
          </Button>
        )}
      </Header>
      {src ? (
        <Iframe
          title={t('email.previewTitle')}
          src={src}
          sandbox="allow-same-origin"
        />
      ) : (
        <EmailBody>
          <Text
            variant="body2"
            dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(emailBody) }}
          />
        </EmailBody>
      )}
    </Container>
  );
};

// The letter's height can't be read (its document is sandboxed to an
// opaque origin), so the iframe fills whatever height the parent gives,
// e.g. BothPreview's row, which the phone makes taller than 480px.
const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const Header = styled.div`
  padding: 12px 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;
// In a narrow column (BothPreview gives it 60%) the button wraps below
// the sender and subject rather than squeezing them, and a long unbroken
// subject wraps rather than pushing the button out.
const Meta = styled.div`
  flex: 1 1 200px;
  min-width: 0;
  overflow-wrap: anywhere;
`;
const From = styled.div`
  margin-bottom: 4px;
`;
const Subject = styled.div``;
const EmailBody = styled.div`
  border-top: 1px solid ${themeGet('colors.black.10')};
  padding: 0px 20px;
  overflow-y: auto;

  ol {
    list-style-type: decimal;
    padding: 10px 20px;
  }
  ul {
    list-style-type: disc;
    padding: 10px 20px;
  }
  img {
    max-width: 100%;
    height: auto;
  }
`;
const Iframe = styled.iframe`
  flex: 1;
  width: 100%;
  min-height: 480px;
  border: 0;
  border-top: 1px solid ${themeGet('colors.black.10')};
`;
